import {
    BoxIcon,
    CalendarIcon,
    CircleInfoIcon,
    ClipboardIcon,
    ImageIcon,
    ProductOptionsIcon,
    QuestionnaireIcon,
    RosterIcon,
    ServiceIcon,
    StackIcon,
    StarIcon,
    UserIcon,
    WaitlistIcon,
    WifiIcon,
} from "@xola/icons";
import React from "react";
import { Experience, Permission } from "types";
import { ExperienceDescriptiveContent } from "../../components/Product/DescriptiveContent/ExperienceDescriptiveContent";
import { ExperienceOptionList } from "../../components/Product/ExperienceOptions/ExperienceOptionList";
import { ExperiencePhotoList } from "../../components/Product/ExperiencePhotoList";
import { ExperienceResourcesSettings } from "../../components/Product/ExperienceResources/ExperienceResourcesSettings";
import { ExperienceReviews } from "../../components/Product/ExperienceReviews";
import { ExperienceVirtualMeeting } from "../../components/Product/ExperienceVirtualMeeting";
import { ExperienceWaitlist } from "../../components/Product/ExperienceWaitlist";
import { AddOnList } from "../../components/Product/GlobalSettings/AddOns/AddOnList";
import { ProductTerms } from "../../components/Product/ProductTerms";
import { ProductQuestionnaire } from "../../components/Product/Questionnaire/ProductQuestionnaire";
import { ExperienceSchedulesAvailability } from "../../components/Product/SchedulesAvailability/ExperienceSchedulesAvailability";
import { ExperienceSelfService } from "../../components/Product/SelfService/ExperienceSelfService";
import { useAuthorization } from "../../hooks/useAuthorization";
import { ExperiencePricingPage } from "./ExperiencePricingPage";
import { GuestManagement } from "@/components/Product/GuestManagement/GuestManagement";

export const useExperienceTabs = (experience?: Experience) => {
    const { isGranted } = useAuthorization();

    /**
     * isGranted does not work here, it will check permission and return children or null. But when this gets rendered,
     * isGranted Children might not be run so the children of isGranted component will always render irrespective
     * of permission validity.
     */
    const showWaitlistPreference = isGranted(Permission.WAITLISTS_VIEW);
    const showResources = isGranted(Permission.RESOURCES_VIEW);
    const showVirtualMeetingTab = isGranted(Permission.VIRTUAL_MEETINGS_VIEW) && !!experience?.virtualMeeting?.enabled;

    const tabs = [
        {
            id: "descriptive",
            label: "Descriptive Content",
            icon: <CircleInfoIcon size="large" />,
            description: "Important details that will be presented to your customer throughout the booking process",
            content: <ExperienceDescriptiveContent experience={experience} />,
        },
        {
            id: "schedules",
            label: "Schedules & Availability",
            icon: <CalendarIcon size="large" />,
            condition: !!experience,
            description: "Build activity schedules that your Experiences is available to be booked by customers",
            content: experience && <ExperienceSchedulesAvailability experience={experience} />,
        },
        {
            id: "photos",
            label: "Photos",
            icon: <ImageIcon size="large" />,
            condition: !!experience,
            description:
                "Set your primary photo which will be set as the image in the back office and in online checkout",
            content: experience && <ExperiencePhotoList experience={experience} />,
        },
        {
            id: "pricing",
            label: "Pricing",
            icon: <StackIcon size="large" />,
            condition: !!experience,
            description: "Set up your experience pricing in a way that matches your business needs",
            content: experience && <ExperiencePricingPage experience={experience} />,
        },
        {
            id: "add-ons",
            label: "Add-ons",
            icon: <BoxIcon size="large" />,
            condition: !!experience,
            description: "Additional items that you sell along with a reservation",
            content: experience && <AddOnList experience={experience} />,
        },
        {
            id: "experience-options",
            label: "Experience Options",
            icon: <ProductOptionsIcon size="large" />,
            condition: !!experience,
            description: "Library of product options that can be attached to schedules",
            content: experience && <ExperienceOptionList experience={experience} />,
        },
        {
            id: "questionnaire",
            label: "Questionnaire",
            icon: <QuestionnaireIcon size="large" />,
            condition: !!experience,
            description: "Collect additional information from your customers in the online checkout or on Xola Waivers",
            content: experience && <ProductQuestionnaire product={experience} />,
        },
        {
            id: "terms",
            label: "Terms",
            icon: <RosterIcon size="large" />,
            condition: !!experience,
            description: "Set up terms, rules and guidelines for your business",
            content: experience && <ProductTerms terms={experience?.terms} />,
        },
        {
            id: "self Service",
            label: "Self Service",
            icon: <ServiceIcon size="large" />,
            condition: !!experience,
            description: "Allow customers to manage their own reservations",
            content: experience && <ExperienceSelfService experience={experience} />,
        },
        {
            id: "waitlist",
            label: "Waitlist",
            condition: !!experience && showWaitlistPreference,
            icon: <WaitlistIcon size="large" />,
            description:
                "Customers will be presented with the option during checkout to 'Join Waitlist' when a time slot that does not have enough open spots to accommodate them",
            content: experience && <ExperienceWaitlist experience={experience} />,
        },
        {
            id: "resources",
            label: "Resources",
            icon: <ClipboardIcon size="large" />,
            condition: !!experience && showResources,
            description: "Set parameters around equipment and/or guides for your experience",
            content: experience && <ExperienceResourcesSettings experience={experience} />,
        },
        {
            id: "reviews",
            label: "Customer Reviews",
            icon: <StarIcon size="large" />,
            condition: !!experience,
            description: "Set up emails that prompts customers to provide feedback on your services",
            content: experience && <ExperienceReviews reviewPreference={experience?.reviewPreference} />,
        },
        {
            id: "guestManagement",
            label: "Guest Management",
            icon: <UserIcon size="large" />,
            condition: !!experience,
            description:
                "Manage ticket configurations, check-in preferences, and other guest related settings for this experience",
            content: experience && <GuestManagement experience={experience} />,
        },
        {
            id: "virtualMeeting",
            label: "Virtual Meeting",
            condition: !!experience && showVirtualMeetingTab,
            icon: <WifiIcon size="large" />,
            description: "Configure your virtual meeting settings",
            content: experience && <ExperienceVirtualMeeting experience={experience} />,
        },
    ];

    return tabs;
};
